// Follow-ups (staff): everyone whose visit is currently "not cleared" (respirator) or "referred", with how long it has been
// open and whether follow-up documents have arrived. Open one to attach documents and record the follow-up decision.
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorBox } from '../components/Layout.tsx';
import { clearanceText } from '../components/FollowUp.tsx';
import { useData } from '../lib/useData.ts';

interface Row {
  assessmentId: string; participantId: string; lastName: string; firstName: string; employeeId: string | null; departmentId: string | null; departmentName: string | null;
  assessedOn: string; status: string; respiratorQualified: boolean | null; referred: boolean | null; referralReason: string | null; source: 'review' | 'follow_up';
  decidedOn: string; daysOpen: number; documents: number; updates: number;
  originalRespiratorQualified: boolean | null; originalReferred: boolean | null; originalReason: string | null;
}

export function Followups() {
  const [all, setAll] = useState(false);
  const { data, error } = useData<Row[]>(`/api/followups${all ? '?all=1' : ''}`);
  const [q, setQ] = useState('');
  const [org, setOrg] = useState('');
  const [kind, setKind] = useState('');
  const nav = useNavigate();
  const orgs = useMemo(() => [...new Map((data ?? []).filter((r) => r.departmentId).map((r) => [r.departmentId!, r.departmentName ?? ''])).entries()]
    .sort((a, b) => a[1].localeCompare(b[1])), [data]);
  const shown = (data ?? []).filter((r) => (!org || r.departmentId === org)
    && (!kind || (kind === 'notcleared' ? r.respiratorQualified === false : kind === 'referred' ? r.referred : kind === 'docs' ? r.documents > 0 && r.source === 'review' : true))
    && (!q || `${r.lastName} ${r.firstName} ${r.employeeId ?? ''}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <>
      <h1>Follow-ups</h1>
      <p className="muted" style={{ marginTop: 0 }}>People whose signed visit is <b>not cleared</b> for respirator use or <b>referred</b>, until a follow-up clears
        them (usually a clearance letter from their own provider). Open one to attach the letter and record the follow-up. Visits from the last two years.</p>
      <ErrorBox error={error} />
      <div className="card ev-filters">
        <input aria-label="Search" placeholder="Name or ID #" value={q} onChange={(e) => setQ(e.target.value)} />
        <select aria-label="Organization" value={org} onChange={(e) => setOrg(e.target.value)}><option value="">All organizations</option>
          {orgs.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select>
        <select aria-label="Show" value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">Not cleared or referred</option><option value="notcleared">Not cleared for respirator</option><option value="referred">Referred</option>
          <option value="docs">Documents arrived, no decision yet</option></select>
        <label className="small"><input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} /> Include closed follow-ups and older visits</label>
      </div>
      <div className="card">
        {!data ? <p className="muted">Loading…</p> : !shown.length ? <p className="muted">No open follow-ups{org || kind || q ? ' match these filters' : ''}.</p> : (
          <div className="table-scroll"><table><thead><tr><th>Name</th><th>Organization</th><th>Visit</th><th>Now</th><th>Open</th><th>Documents</th></tr></thead>
            <tbody>{shown.map((r) => (
              <tr key={r.assessmentId} className="link" onClick={() => nav(`/assessments/${r.assessmentId}`)}>
                <td><Link to={`/participants/${r.participantId}`} onClick={(e) => e.stopPropagation()}>{r.lastName}, {r.firstName}</Link>{r.employeeId && <div className="muted small">ID {r.employeeId}</div>}</td>
                <td>{r.departmentName ?? '—'}</td>
                <td className="nowrap">{r.assessedOn}</td>
                <td>{clearanceText(r)}{r.source === 'follow_up' && <div className="muted small">follow-up decision {r.decidedOn}</div>}</td>
                <td className="nowrap">{r.daysOpen} days</td>
                <td>{r.documents ? `${r.documents}` : <span className="muted">none yet</span>}{r.updates > 0 && <div className="muted small">{r.updates} decision{r.updates === 1 ? '' : 's'}</div>}</td>
              </tr>))}</tbody></table></div>)}
      </div>
    </>
  );
}
